// Balíček dat na směnu (F4 4.5): tablet si dnešní jízdy, zákazníky, položky a body ranní kontroly stáhne
// **dopředu** — po přihlášení se pak v Můj den nic nedotahuje ze sítě a řidič může pracovat celou směnu bez
// signálu (ověřujeme na aspoň 2 hodinách; rozhodnutí zadavatele 15. 9. 2026, 05/20).
//
// Balíček se stahuje po přihlášení a pak v pauzách, dokud je signál; uloží se do IndexedDB (přežije zavření
// prohlížeče i restart tabletu). Bez signálu z něj Můj den čte přehled dne i položky jízd.
import type { Schema } from '@ecoser/shared';
import { api } from '../api/klient';
import type { MujDenPrehled, MujDenVozidlo } from '../api/muj-den';
import type { ZakazkaPolozkaKartaSeznam } from '../api/zakazky';

/** Karta jízdy tak, jak ji nese přehled dne v balíčku. */
type ZakazkaKartaBalicku = MujDenPrehled['zakazky'][number];
import { predstahniKatalogProTeren } from '../komponenty/seznam-polozek-katalogu';
import { jeOffline } from './sit';
import { uloziste } from './uloziste';

export type BalicekOffline = Omit<Schema<'OfflineBalicek'>, 'muj_den' | 'polozky'> & {
  muj_den: MujDenPrehled;
  polozky: Array<{ zakazka_id: string } & ZakazkaPolozkaKartaSeznam>;
};

const KLIC = 'balicek';

/** Poslední načtený balíček v paměti — fronta a cache z něj čtou i bez čekání na IndexedDB. */
let vPameti: BalicekOffline | undefined;

/** Kdo chce vědět, že se balíček změnil (obrazovky bez signálu čtou z něj — musí se překreslit). */
const posluchaci = new Set<(balicek: BalicekOffline | undefined) => void>();

function oznam(): void {
  for (const p of posluchaci) p(vPameti);
}

/** Přihlásí posluchače změn balíčku; vrací odhlášení. */
export function sledujBalicek(
  posluchac: (balicek: BalicekOffline | undefined) => void,
): () => void {
  posluchaci.add(posluchac);
  return () => posluchaci.delete(posluchac);
}

/** Balíček, který má tablet právě po ruce (bez čtení z úložiště). */
export function balicekVPameti(): BalicekOffline | undefined {
  return vPameti;
}

/** Stáhne balíček na daný den a uloží ho do tabletu. Bez signálu nedělá nic (vrátí uložený). */
export async function stahniBalicek(datum?: string): Promise<BalicekOffline | undefined> {
  if (jeOffline()) return nactiBalicek();
  const dotaz = datum ? `?datum=${encodeURIComponent(datum)}` : '';
  // Dotažení na pozadí smí tiše selhat — z neúspěchu se nedělá „bez signálu“ (to řeší skutečná práce řidiče).
  const balicek = await api.get<BalicekOffline>(`/offline/balicek${dotaz}`, {
    bezHlaseniSite: true,
  });
  await uloziste().uloz(KLIC, balicek);
  vPameti = balicek;
  oznam();
  // Katalogové seznamy a položky ze smluv zákazníků dnešních jízd pro „Přidat položku“ bez signálu (27. 10. 2026);
  // na pozadí, nic nečeká a chyby se přeskočí.
  void predstahniKatalogProTeren(balicek.muj_den?.zakazky ?? []);
  return balicek;
}

/** Balíček uložený v tabletu (nebo undefined, když se ještě nestáhl). */
export async function nactiBalicek(): Promise<BalicekOffline | undefined> {
  const balicek = await uloziste().ziskej<BalicekOffline>(KLIC);
  if (balicek) {
    vPameti = balicek;
    oznam();
  }
  return balicek;
}

/**
 * Zapomene balíček v paměti i v tabletu — po změně identity (začátek a konec náhledu za uživatele) patří cizímu
 * účtu. Obrazovky, které z něj čtou, se překreslí; nový se stáhne při dalším otevření Můj den.
 */
export async function zapomenBalicek(): Promise<void> {
  vPameti = undefined;
  oznam();
  await uloziste()
    .smaz(KLIC)
    .catch(() => {});
}

/** Jen pro testy: zapomene balíček v paměti. */
export function vynulujBalicek(): void {
  vPameti = undefined;
  posluchaci.clear();
}

/** Přehled dne z balíčku — jen pro den, na který byl stažený (jinak by tablet ukázal cizí den). */
export function prehledZBalicku(
  balicek: BalicekOffline | undefined,
  datum: string,
): MujDenPrehled | undefined {
  if (!balicek || balicek.datum !== datum) return undefined;
  return balicek.muj_den;
}

/** Položky jízdy z balíčku (doplňování množství bez signálu). */
export function polozkyZBalicku(
  balicek: BalicekOffline | undefined,
  zakazkaId: string,
): ZakazkaPolozkaKartaSeznam | undefined {
  const nalezene = balicek?.polozky.find((p) => p.zakazka_id === zakazkaId);
  if (!nalezene) return undefined;
  const { zakazka_id: _id, ...seznam } = nalezene;
  return seznam;
}

/**
 * Zapíše do balíčku, co řidič udělal bez signálu (karta jízdy a položky). Bez toho by obrazovky, které
 * bez signálu čtou z balíčku (Můj den, detail jízdy, podpis), ukazovaly pořád stav ze stažení — řidič
 * doplnil množství a podpis by mu pak tvrdil, že množství chybí.
 */
export function ulozDoBalicku(zmena: {
  karta?: ZakazkaKartaBalicku;
  polozky?: { zakazkaId: string; seznam: ZakazkaPolozkaKartaSeznam };
  /** Ranní kontrola zapsaná bez signálu (05/1564): vozidlo dne už není „bez kontroly“, pruh v Můj den zezelená. */
  kontrola?: { vozidloId: string; stav: NonNullable<MujDenVozidlo['kontrola_stav']> };
}): void {
  if (!vPameti) return;
  const novy: BalicekOffline = { ...vPameti };
  if (zmena.karta) {
    const karta = zmena.karta;
    novy.muj_den = {
      ...novy.muj_den,
      zakazky: novy.muj_den.zakazky.map((z) => (z.id === karta.id ? karta : z)),
    };
  }
  if (zmena.polozky) {
    const { zakazkaId, seznam } = zmena.polozky;
    novy.polozky = novy.polozky.map((p) =>
      p.zakazka_id === zakazkaId ? { ...seznam, zakazka_id: zakazkaId } : p,
    );
  }
  if (zmena.kontrola) {
    const { vozidloId, stav } = zmena.kontrola;
    novy.muj_den = {
      ...novy.muj_den,
      vozidla_dnes: novy.muj_den.vozidla_dnes.map((v) =>
        v.vozidlo_id === vozidloId ? { ...v, kontrola_stav: stav } : v,
      ),
    };
  }
  vPameti = novy;
  oznam();
  // Uložení je „nejlépe jak to jde“: v paměti je změna hned, do IndexedDB doteče na pozadí.
  void uloziste()
    .uloz(KLIC, novy)
    .catch(() => {});
}

/**
 * Zahodí z balíčku zakázky, které server už nedává (smazané do koše, přehozené jinam — W102 „smazal jsem ji a skladník
 * ji pořád vidí“): jejich karty v přehledu dne, historii i nedokončených pokladnách a jejich položky. Bez toho by je
 * tablet ukazoval z balíčku dál (bez signálu, detail jízdy po 404). Nic jiného se nemění.
 */
export function zahodZBalicku(zakazkaIds: Iterable<string>): void {
  const ids = new Set(zakazkaIds);
  if (!vPameti || ids.size === 0) return;
  const md = vPameti.muj_den;
  const ven = <T extends { id: string }>(seznam: readonly T[] | undefined) =>
    seznam?.filter((z) => !ids.has(z.id));
  const zakazky = ven(md.zakazky) ?? [];
  const polozky = vPameti.polozky.filter((p) => !ids.has(p.zakazka_id));
  const historie = ven(md.historie);
  const pokladny = ven(md.nedokoncene_pokladny);
  const zmeneno =
    zakazky.length !== md.zakazky.length ||
    polozky.length !== vPameti.polozky.length ||
    historie?.length !== md.historie?.length ||
    pokladny?.length !== md.nedokoncene_pokladny?.length;
  if (!zmeneno) return;
  vPameti = {
    ...vPameti,
    muj_den: {
      ...md,
      zakazky,
      ...(historie ? { historie } : {}),
      ...(pokladny ? { nedokoncene_pokladny: pokladny } : {}),
    },
    polozky,
  };
  oznam();
  void uloziste()
    .uloz(KLIC, vPameti)
    .catch(() => {});
}

/**
 * Čerstvý přehled dne ze serveru (stejný dotaz, jaký skládá balíček: den bez „Všichni řidiči“ a bez rozsahu dnů) —
 * co v něm na den balíčku chybí, z balíčku zmizí (W102). Jiný den nebo jiný dotaz balíček nechá být.
 */
export function srovnejBalicekSPrehledem(prehled: Pick<MujDenPrehled, 'datum' | 'zakazky'>): void {
  if (!vPameti || vPameti.datum !== prehled.datum) return;
  const naServeru = new Set(prehled.zakazky.map((z) => z.id));
  zahodZBalicku(vPameti.muj_den.zakazky.map((z) => z.id).filter((id) => !naServeru.has(id)));
}

/** Jak je balíček starý (v minutách); undefined = žádný. */
export function stariBalicku(balicek: BalicekOffline | undefined): number | undefined {
  if (!balicek?.porizeno) return undefined;
  return Math.round((Date.now() - new Date(balicek.porizeno).getTime()) / 60_000);
}
